(function () {
  /**
   * Phone+OTP sign-in / registration flow — mirrors mobile_app's
   * CommercePhoneAuthPanel (signup: name+phone+gender+birthday → OTP;
   * login: phone → OTP). Mounts into any <div data-eros-auth-panel>.
   *
   * Wave 24D: signup always collects profile fields before OTP (Flutter
   * PhoneAuthSignupFields order). phoneCheck no longer auto-skips signup
   * fields when a new phone is mis-marked exists — signup tab shows the
   * full form; exists is only used to block register / gate login OTP.
   */

  var STR = (window.ErosAccountStrings && window.ErosAccountStrings.auth) || {};
  var ASSETS = (window.ErosAccountStrings && window.ErosAccountStrings.assets) || {};
  // Offline-aware failure copy (mirrors Flutter's _networkAwareError,
  // owner order 2026-08-25): a fetch TypeError / navigator.onLine === false
  // means the request never reached the server — blame connectivity, not
  // the phone number or the code. Neutral 'info' tone for the offline line,
  // mirroring the app's white (non-red) treatment.
  function isNetworkError(err) {
    return navigator.onLine === false || err instanceof TypeError;
  }
  function failureToast(err) {
    if (isNetworkError(err)) {
      window.ErosUI.toast(t('offline_error', 'لا يوجد اتصال بالإنترنت'), 'info');
      return;
    }
    window.ErosUI.toast(t('generic_error', 'حدث خطأ، حاول مرة أخرى'), 'error');
  }

  function t(key, fallback) {
    return STR[key] || fallback;
  }

  function normalizeDigits(s) {
    // Accepts Eastern Arabic numerals too (mirrors server-side normalization intent).
    // ADR-320: Persian ۰-۹ too, as the code boxes do.
    return String(s || '')
      .replace(/[\u0660-\u0669]/g, function (d) { return String(d.charCodeAt(0) - 0x0660); })
      .replace(/[\u06f0-\u06f9]/g, function (d) { return String(d.charCodeAt(0) - 0x06f0); });
  }

  /** Iraq mobile national 10 digits starting with 7 (IraqPhone.parseMobile intent). */
  function parseIraqMobileDigits(raw) {
    var phone = normalizeDigits(raw).replace(/[^0-9]/g, '');
    if (phone.indexOf('964') === 0) phone = phone.slice(3);
    if (phone.charAt(0) === '0') phone = phone.slice(1);
    if (phone.length !== 10 || phone.charAt(0) !== '7') return null;
    return phone;
  }

  /** Same e164 shape Flutter CommerceAuthApi sends to the Worker. */
  function toE164(phoneCore) {
    return '+964' + phoneCore;
  }

  /** Strict boolean — avoids truthy misreads if Worker payload drifts. */
  function phoneExists(res) {
    return !!(res && res.exists === true);
  }

  function buildPanel(root) {
    var state = {
      step: 'phone', // phone | signup | otp | done
      // LOGIN-first everywhere (owner order 2026-08-25, mirrors Flutter's
      // CommercePhoneAuthPanel.initialMode): every auth entry opens on
      // phone-number sign-in; the signup tab stays one tap away.
      mode: 'login',
      phone: '',
      signupToken: null,
      signup: null, // {firstName, gender, birthday} so a signup code can be re-sent
      resendAt: 0,
    };

    // ADR-176 (mirrors Flutter commerce_phone_auth_panel): 60 s after every
    // send before "resend", or the server's retryAfterSeconds after a 429.
    var RESEND_COOLDOWN_SEC = 60;
    function retryAfterOf(err) {
      if (!err || err.status !== 429) return 0;
      var sec = err.details && err.details.retryAfterSeconds;
      return sec > 0 ? Math.ceil(sec) : 300;
    }
    function secondsLeft() {
      return Math.max(0, Math.ceil((state.resendAt - Date.now()) / 1000));
    }
    function rateLimitedToast(wait) {
      window.ErosUI.toast(
        t('otp_rate_limited', 'أرسلنا لك عدة رموز — يمكنك المحاولة مجدداً بعد') + ' ' + wait + ' ' + t('seconds_short', 'ث'),
        'error',
      );
    }
    /** Keeps `btn` disabled with a countdown label until state.resendAt; restores `idleText` after. */
    function countdown(btn, idleText, fmt) {
      if (btn._erosTimer) clearInterval(btn._erosTimer);
      function tick() {
        var left = secondsLeft();
        if (left > 0) {
          btn.disabled = true;
          btn.textContent = fmt(left);
          return;
        }
        clearInterval(btn._erosTimer);
        btn._erosTimer = null;
        btn.disabled = false;
        btn.textContent = idleText;
      }
      tick();
      if (secondsLeft() > 0) btn._erosTimer = setInterval(tick, 1000);
    }
    function retryFmt(n) {
      return t('retry_in', 'حاول بعد') + ' ' + n + ' ' + t('seconds_short', 'ث');
    }

    root.innerHTML = '';

    var logoUrl = ASSETS.logoUrl || '';
    if (logoUrl) {
      var logo = window.ErosUI.el('img', 'eros-auth-panel__logo', {
        src: logoUrl,
        alt: '',
        width: '120',
        height: '48',
      });
      root.appendChild(logo);
    }

    var segments = window.ErosUI.el('div', 'eros-auth-panel__segments', {
      role: 'tablist',
      'aria-label': t('mode_label', 'Account'),
    });
    var loginSeg = window.ErosUI.el('button', 'eros-auth-panel__segment', {
      type: 'button',
      role: 'tab',
    });
    loginSeg.textContent = t('login_tab', 'تسجيل الدخول');
    var signupSeg = window.ErosUI.el('button', 'eros-auth-panel__segment is-selected', {
      type: 'button',
      role: 'tab',
    });
    signupSeg.textContent = t('signup_tab', 'إنشاء حساب');
    segments.appendChild(loginSeg);
    segments.appendChild(signupSeg);
    root.appendChild(segments);

    var subtitle = window.ErosUI.el('p', 'eros-auth-panel__subtitle');
    root.appendChild(subtitle);

    var stepHost = window.ErosUI.el('div', 'eros-auth-panel__step');
    root.appendChild(stepHost);

    function syncChrome() {
      var onOtp = state.step === 'otp';
      segments.hidden = onOtp;
      subtitle.hidden = onOtp;
      if (!onOtp) {
        loginSeg.classList.toggle('is-selected', state.mode === 'login');
        signupSeg.classList.toggle('is-selected', state.mode === 'signup');
        loginSeg.setAttribute('aria-selected', state.mode === 'login' ? 'true' : 'false');
        signupSeg.setAttribute('aria-selected', state.mode === 'signup' ? 'true' : 'false');
        subtitle.textContent =
          state.mode === 'login'
            ? t('subtitle_login', t('instructions', ''))
            : t('subtitle_signup', t('instructions', ''));
      }
    }

    function showModeForm() {
      if (state.mode === 'signup') {
        renderSignupStep();
      } else {
        renderPhoneStep();
      }
    }

    loginSeg.addEventListener('click', function () {
      if (state.step === 'otp') return;
      state.mode = 'login';
      state.signupToken = null;
      renderPhoneStep();
    });
    signupSeg.addEventListener('click', function () {
      if (state.step === 'otp') return;
      state.mode = 'signup';
      state.signupToken = null;
      renderSignupStep();
    });

    /** Login: phone only → phoneCheck → OTP (Flutter _sendLoginOtpOnly). */
    function renderPhoneStep() {
      state.step = 'phone';
      state.mode = 'login';
      syncChrome();
      stepHost.innerHTML = '';

      var form = window.ErosUI.el('form', 'eros-auth-form');
      var field = window.ErosUI.el('div', 'form-control');
      var label = window.ErosUI.el('label');
      label.textContent = t('phone_label', 'رقم الهاتف');
      label.setAttribute('for', 'eros-auth-phone');
      var input = window.ErosUI.el('input', 'input', {
        type: 'tel',
        inputmode: 'numeric',
        id: 'eros-auth-phone',
        name: 'phone',
        placeholder: '07xxxxxxxx',
        autocomplete: 'tel',
      });
      if (state.phone) input.value = state.phone.length === 10 ? '0' + state.phone : state.phone;
      field.appendChild(label);
      field.appendChild(input);
      form.appendChild(field);

      var submit = window.ErosUI.el('button', 'button', { type: 'submit' });
      submit.textContent = t('continue', 'متابعة');
      form.appendChild(submit);

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var phone = parseIraqMobileDigits(input.value);
        if (!phone) {
          window.ErosUI.setFieldError(input, t('phone_invalid', 'رقم الهاتف غير صحيح'));
          return;
        }
        window.ErosUI.setFieldError(input, null);
        submit.disabled = true;
        var e164 = toE164(phone);
        window.ErosCommerce.phoneCheck(e164)
          .then(function (res) {
            state.phone = phone;
            if (!phoneExists(res)) {
              // ADR-179: jump to the signup tab with the phone kept, and say why.
              window.ErosUI.toast(
                t('phone_not_registered', 'لا يوجد حساب بهذا الرقم — أكمل بياناتك لإنشاء حساب'),
                'error',
              );
              renderSignupStep();
              return;
            }
            return window.ErosCommerce.otpSend(e164).then(function (sendRes) {
              state.resendAt = Date.now() + RESEND_COOLDOWN_SEC * 1000;
              state.signupToken = null;
              renderOtpStep();
            });
          })
          .catch(function (err) {
            var wait = retryAfterOf(err);
            if (wait) {
              state.resendAt = Date.now() + wait * 1000;
              rateLimitedToast(wait);
              countdown(submit, submit.textContent, retryFmt);
              return;
            }
            failureToast(err);
          })
          .finally(function () {
            if (secondsLeft() === 0) submit.disabled = false;
          });
      });

      stepHost.appendChild(form);
      input.focus();
    }

    /**
     * Signup: Flutter PhoneAuthSignupFields order — name, phone, gender, birthday —
     * on one step before OTP. Never skip this when the signup tab is active.
     */
    function renderSignupStep() {
      state.step = 'signup';
      state.mode = 'signup';
      syncChrome();
      stepHost.innerHTML = '';

      var form = window.ErosUI.el('form', 'eros-auth-form');

      var nameField = window.ErosUI.el('div', 'form-control');
      var nameLabel = window.ErosUI.el('label');
      nameLabel.textContent = t('first_name_label', 'الاسم');
      nameLabel.setAttribute('for', 'eros-auth-first-name');
      var nameInput = window.ErosUI.el('input', 'input', {
        type: 'text',
        id: 'eros-auth-first-name',
        name: 'firstName',
        autocomplete: 'given-name',
        placeholder: t('first_name_label', 'الاسم'),
      });
      nameField.appendChild(nameLabel);
      nameField.appendChild(nameInput);

      var phoneField = window.ErosUI.el('div', 'form-control');
      var phoneLabel = window.ErosUI.el('label');
      phoneLabel.textContent = t('phone_label', 'رقم الهاتف');
      phoneLabel.setAttribute('for', 'eros-auth-signup-phone');
      var phoneInput = window.ErosUI.el('input', 'input', {
        type: 'tel',
        inputmode: 'numeric',
        id: 'eros-auth-signup-phone',
        name: 'phone',
        placeholder: '07xxxxxxxx',
        autocomplete: 'tel',
      });
      if (state.phone) {
        phoneInput.value = state.phone.length === 10 ? '0' + state.phone : state.phone;
      }
      phoneField.appendChild(phoneLabel);
      phoneField.appendChild(phoneInput);

      var genderField = window.ErosUI.el('div', 'form-control');
      var genderLabel = window.ErosUI.el('label');
      genderLabel.textContent = t('gender_label', 'الجنس');
      genderLabel.setAttribute('for', 'eros-auth-gender');
      var genderSelect = window.ErosUI.el('select', 'select', {
        id: 'eros-auth-gender',
        name: 'gender',
      });
      // Values must match ShopifyIdentityGenderValues exactly — see
      // eros-account-profile.js for the same note.
      [
        ['', t('gender_placeholder', 'اختر الجنس')],
        ['انثى', t('gender_female', 'أنثى')],
        ['ذكر', t('gender_male', 'ذكر')],
      ].forEach(function (opt) {
        var o = document.createElement('option');
        o.value = opt[0];
        o.textContent = opt[1];
        genderSelect.appendChild(o);
      });
      genderField.appendChild(genderLabel);
      genderField.appendChild(genderSelect);

      var birthdayField = window.ErosUI.el('div', 'form-control');
      var birthdayLabel = window.ErosUI.el('label');
      birthdayLabel.textContent = t('birthday_label', 'تاريخ الميلاد');
      birthdayLabel.setAttribute('for', 'eros-auth-birthday');
      // Hidden input keeps the existing validation/submission code (below)
      // unchanged — the wheel picker below is purely a visual replacement
      // for the native <input type="date"> UI, matching Flutter's inline
      // CupertinoPicker triple wheel instead of a calendar-grid dialog.
      var birthdayInput = window.ErosUI.el('input', 'input', {
        type: 'hidden',
        id: 'eros-auth-birthday',
        name: 'birthday',
      });
      // Flutter defaults birthday ~18 years ago (Jan 15).
      var defaultYear = new Date().getFullYear() - 18;
      birthdayInput.value = defaultYear + '-01-15';
      birthdayField.appendChild(birthdayLabel);
      birthdayField.appendChild(birthdayInput);
      if (window.ErosBirthdayWheel) {
        var birthdayWheelHost = window.ErosUI.el('div');
        birthdayField.appendChild(birthdayWheelHost);
        window.ErosBirthdayWheel.mount(birthdayWheelHost, {
          value: new Date(birthdayInput.value + 'T00:00:00'),
          onChange: function (dateStr) {
            birthdayInput.value = dateStr;
          },
        });
      } else {
        birthdayInput.type = 'date';
      }

      // Flutter order: name → phone → gender → birthday
      form.appendChild(nameField);
      form.appendChild(phoneField);
      form.appendChild(genderField);
      form.appendChild(birthdayField);

      var submit = window.ErosUI.el('button', 'button', { type: 'submit' });
      submit.textContent = t('register_send_otp', t('continue', 'إنشاء حساب وإرسال الرمز'));
      form.appendChild(submit);

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var firstName = nameInput.value.trim();
        var phone = parseIraqMobileDigits(phoneInput.value);
        var gender = genderSelect.value;
        var birthday = birthdayInput.value; // <input type=date> already yields YYYY-MM-DD

        var ok = true;
        if (!firstName) {
          window.ErosUI.setFieldError(nameInput, t('required', 'مطلوب'));
          ok = false;
        } else {
          window.ErosUI.setFieldError(nameInput, null);
        }
        if (!phone) {
          window.ErosUI.setFieldError(phoneInput, t('phone_invalid', 'رقم الهاتف غير صحيح'));
          ok = false;
        } else {
          window.ErosUI.setFieldError(phoneInput, null);
        }
        if (!gender) {
          window.ErosUI.setFieldError(genderSelect, t('required', 'مطلوب'));
          ok = false;
        } else {
          window.ErosUI.setFieldError(genderSelect, null);
        }
        if (!/^\d{4}-\d{2}-\d{2}$/.test(birthday)) {
          window.ErosUI.setFieldError(birthdayInput, t('required', 'مطلوب'));
          ok = false;
        } else {
          window.ErosUI.setFieldError(birthdayInput, null);
        }
        if (!ok) return;

        submit.disabled = true;
        state.phone = phone;
        var e164 = toE164(phone);

        // Mirror Flutter _startOtpFlow signup: check → register → OTP.
        // Do NOT skip profile collection when exists is true — user already
        // filled the form; toast + switch to login instead of silent OTP.
        window.ErosCommerce.phoneCheck(e164)
          .then(function (res) {
            if (phoneExists(res)) {
              window.ErosUI.toast(
                t(
                  'phone_already_registered',
                  'هذا الرقم مسجّل مسبقاً — سجّل الدخول من هنا',
                ),
                'error',
              );
              state.mode = 'login';
              renderPhoneStep();
              return null;
            }
            return window.ErosCommerce.register(e164, firstName, gender, birthday);
          })
          .then(function (res) {
            if (!res) return;
            state.signupToken = res.signupToken;
            state.signup = { firstName: firstName, gender: gender, birthday: birthday };
            state.resendAt = Date.now() + RESEND_COOLDOWN_SEC * 1000;
            renderOtpStep();
          })
          .catch(function (err) {
            var wait = retryAfterOf(err);
            if (wait) {
              state.resendAt = Date.now() + wait * 1000;
              rateLimitedToast(wait);
              countdown(submit, submit.textContent, retryFmt);
              return;
            }
            if (err && err.code === 'phone_already_registered') {
              window.ErosUI.toast(
                t(
                  'phone_already_registered',
                  'هذا الرقم مسجّل مسبقاً — سجّل الدخول من هنا',
                ),
                'error',
              );
              state.mode = 'login';
              renderPhoneStep();
              return;
            }
            failureToast(err);
          })
          .finally(function () {
            if (secondsLeft() === 0) submit.disabled = false;
          });
      });

      stepHost.appendChild(form);
      nameInput.focus();
    }

    function renderOtpStep() {
      state.step = 'otp';
      syncChrome();
      stepHost.innerHTML = '';

      var wrap = window.ErosUI.el('div', 'eros-otp-step');
      var hint = window.ErosUI.el('p', 'eros-otp-step__hint');
      var displayPhone = state.phone.length === 10 ? '0' + state.phone : state.phone;
      hint.textContent = t('otp_sent_to', 'أرسلنا رمز التحقق إلى') + ' ' + displayPhone;
      wrap.appendChild(hint);
      var smsHint = window.ErosUI.el('p', 'eros-otp-step__hint');
      smsHint.textContent = t('otp_sms_fallback', 'قد يستغرق وصول الرمز دقيقة.');
      wrap.appendChild(smsHint);

      var otpContainer = window.ErosUI.el('div');
      wrap.appendChild(otpContainer);

      var error = window.ErosUI.el('p', 'eros-field__error');
      wrap.appendChild(error);

      var resend = window.ErosUI.el('button', 'eros-otp-step__resend', { type: 'button' });
      var resendIdle = t('resend', 'إعادة إرسال الرمز');
      var resendFmt = function (n) {
        return t('resend_in', 'إعادة الإرسال بعد') + ' ' + n + ' ' + t('seconds_short', 'ث');
      };
      resend.textContent = resendIdle;
      wrap.appendChild(resend);
      countdown(resend, resendIdle, resendFmt);

      resend.addEventListener('click', function () {
        if (secondsLeft() > 0) return;
        resend.disabled = true;
        var e164 = toE164(state.phone);
        // A signup code is sent by register(); the customer does not exist
        // until verify, so repeating it never answers phone_already_registered.
        var fn = state.signupToken
          ? (state.signup
              ? window.ErosCommerce.register(e164, state.signup.firstName, state.signup.gender, state.signup.birthday)
              : null)
          : window.ErosCommerce.otpSend(e164);
        if (!fn) {
          window.ErosUI.toast(t('signup_expired', 'انتهت صلاحية الجلسة، ابدأ من جديد'), 'error');
          showModeForm();
          return;
        }
        fn
          .then(function (res) {
            if (res && res.signupToken) state.signupToken = res.signupToken;
            state.resendAt = Date.now() + RESEND_COOLDOWN_SEC * 1000;
            window.ErosUI.toast(t('otp_resent', 'تم إرسال رمز جديد'), 'info');
          })
          .catch(function (err) {
            var wait = retryAfterOf(err);
            if (wait) {
              state.resendAt = Date.now() + wait * 1000;
              rateLimitedToast(wait);
              return;
            }
            failureToast(err);
          })
          .finally(function () {
            countdown(resend, resendIdle, resendFmt);
          });
      });

      stepHost.appendChild(wrap);

      // ADR-208 (mirrors Flutter): 4 wrong codes lock this phone/network for
      // 15 min; the server's wait is counted down here and no code is sent
      // until it ends (was «رمز غير صحيح» on every try).
      var lockUntil = 0;
      var lockTimer = null;
      function lockLeft() {
        return Math.max(0, Math.ceil((lockUntil - Date.now()) / 1000));
      }
      function mmss(sec) {
        var m = Math.floor(sec / 60);
        var s = sec % 60;
        return (m < 10 ? '0' : '') + m + ':' + (s < 10 ? '0' : '') + s;
      }
      function showLock() {
        if (lockTimer) clearInterval(lockTimer);
        function tick() {
          var left = lockLeft();
          if (left > 0) {
            error.textContent = t('otp_locked', 'أدخلت رمزاً خاطئاً عدة مرات — حاول مجدداً بعد') + ' ' + mmss(left);
            return;
          }
          clearInterval(lockTimer);
          lockTimer = null;
          error.textContent = '';
        }
        tick();
        lockTimer = setInterval(tick, 1000);
      }

      // maxPasteLength: staff paste the longer master code here (ADR-208); label: ADR-320.
      var otpCtl = window.ErosUI.buildOtpInput(otpContainer, 6, function (code) {
        if (lockLeft() > 0) {
          otpCtl.clear();
          return;
        }
        error.textContent = '';
        var e164 = toE164(state.phone);
        var cartLines = window.ErosCartSync && window.ErosCartSync.deviceLines ? window.ErosCartSync.deviceLines() : Promise.resolve(null);
        cartLines
          .then(function (lines) { return window.ErosCommerce.otpVerify(e164, code, state.signupToken, lines); })
          .then(function (res) {
            window.ErosSession.set({ token: res.token, customerGid: res.customerGid, e164: res.e164 });
            state.step = 'done';
            if (res.mergedCount) {
              window.ErosUI.toast(t('merged_orders', 'تم ربط طلباتك السابقة بحسابك'), 'info');
            }
            window.ErosUI.toast(t('welcome', 'تم تسجيل الدخول بنجاح'), 'info');
            if (typeof root.erosOnAuthComplete === 'function') root.erosOnAuthComplete();
          })
          .catch(function (err) {
            if (err && err.status === 429) {
              var sec = err.details && err.details.retryAfterSeconds;
              lockUntil = Date.now() + (sec > 0 ? Math.ceil(sec) : 900) * 1000;
              showLock();
              otpCtl.clear();
              return;
            }
            error.textContent = isNetworkError(err)
              ? t('offline_error', 'لا يوجد اتصال بالإنترنت')
              : t('otp_invalid', 'رمز غير صحيح، حاول مرة أخرى');
            otpCtl.clear();
          });
      }, { maxPasteLength: 32, label: t('otp_field_label', 'رمز التحقق، 6 أرقام') });

      otpCtl.focus();
    }

    // LOGIN-first (owner order 2026-08-25, mirrors Flutter's
    // CommercePhoneAuthPanel default) — the stale signup-first init here was
    // exactly why /pages/account opened on إنشاء حساب.
    renderPhoneStep();
  }

  function mountAll() {
    var panels = document.querySelectorAll('[data-eros-auth-panel]');
    for (var i = 0; i < panels.length; i++) {
      if (panels[i].dataset.erosAuthMounted) continue;
      panels[i].dataset.erosAuthMounted = 'true';
      buildPanel(panels[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountAll);
  } else {
    mountAll();
  }

  window.ErosAccountAuth = { mountAll: mountAll };
})();
